<template>
	<div
		class="message-outer-container"
		:class="{
			highlighted: message.isHighlighted,
			'no-animate': isEditing,
			editing: isEditing,
			trimmed: trimActionArea,
		}"
	>
		<DateDivider
			v-if="showDateDivider"
			:date="message.created_at"
			:needsThreadLine="needsThreadLine && !isStartOfThreadSegment && !isParent"
		/>

		<ParentQuote
			v-if="isStartOfThreadSegment"
			:messageId="parentMessage && parentMessage.id"
			@open="$emit('thread')"
		/>

		<Accessible
			:name="idPrefix + 'message'"
			arrows="invert"
			:tabOrder="accessibilityTabOrder"
			@enter="openAccessibleActions"
			@esc="closeAccessibleActions"
		>
			<div
				class="message-wrapper"
				:class="{
					stacked: isStacked,
					failedwrapper: message.isFailed,
					'message-hover': showBackdrop,
					'thread-msg-hover': messageHover && needsThreadLine,
					'urlcard-only': hasUrlCardOnly(),
				}"
				@mouseover="onMouseEnter"
				@mouseleave="onMouseLeave"
				:id="elid"
				ref="messageWrapper"
			>
				<div
					v-if="!message.systemevent || isStyledSystemEvent"
					class="message-container"
				>
					<ProfilePic
						v-if="!isStacked && user && !message.isFailed"
						:user="user"
						:enableOnline="user.id !== store.auth.user.id"
						onlineSize="15px"
						size="46px"
						class="message-img"
						@click="buildProfileCard(user)"
					/>
					<CircleExclamationIcon
						v-if="message.isFailed"
						class="message-img"
						type="regular"
					/>
					<div
						class="message"
						:class="{
							'message-embedded': store.config.embedded,
							'overflow-unset': isEditing && inMeeting,
						}"
					>
						<div class="message-header" v-if="!isStacked">
							<UserName
								v-if="user"
								:user="user"
								class="message-name pointer"
								@click="buildProfileCard(user)"
							/>
							<MessageTime :date="message.created_at" />

							<div class="pinned-banner" v-if="isPinned">
								<ThumbtackIcon class="pin-icon" type="solid" />
								{{ $t('chat_drawer_section_pinned') }}
							</div>
						</div>

						<MessageSendingState
							:message="message"
							:class="{
								'editing-message': isEditing || message.isTranslated,
							}"
						>
							<div class="message-text" ref="messageText">
								<MessageMedia :message="message" />

								<div
									v-if="isStyledSystemEvent && !systemLinkMessage"
									class="message-text-system-new"
								>
									{{ systemMessage }}
								</div>

								<div
									v-if="
										isStyledSystemEvent &&
										systemLinkMessage &&
										message.systemevent !== 'BUBBLE_ADDED_MANY'
									"
									class="message-text-system-new"
								>
									{{ systemMessagePrefix }}
									<span
										v-if="message.systemmessageparts"
										@click="getProfileCard(message.systemmessageparts.who_id)"
										>{{ systemMessageSuffix }}</span
									>
								</div>

								<div
									v-if="
										isStyledSystemEvent &&
										systemLinkMessage &&
										message.systemevent === 'BUBBLE_ADDED_MANY'
									"
									class="message-text-system-new"
								>
									<span
										v-if="message.systemmessageparts"
										@click="
											getProfileCard(message.systemmessageparts.invitedby_id)
										"
										>{{ message.systemmessageparts.invitedby_name }}</span
									>
									{{ systemMessage }}
								</div>
								<div
									v-if="!isTaskRemoved && showTaskCard"
									class="task-card pointer"
									@click="getTaskDetail(message.task)"
								>
									<div
										class="task-card-top"
										v-bind:class="{
											'task-card-top-completed': taskIsCompleted,
										}"
									>
										<div
											v-if="!taskIsCompleted"
											id="taskCompleteCheckbox"
											class="task-card-check pointer"
											@click.stop="completeTask(message.task)"
										></div>
										<div
											v-if="taskIsCompleted"
											id="taskUnCompleteCheckbox"
											class="task-card-check pointer"
											@click.stop="unCompleteTask(message.task)"
										></div>

										<span id="task-card-span">{{ message.task.title }}</span>
										<img
											id="task-card-arrow"
											class="task-card-arrow"
											src="/images/common/task-list-arrow.png"
											alt="Task List"
										/>
									</div>
									<div
										class="task-card-bottom task-card-bottom-corners"
										v-bind:class="{
											'task-card-bottom-nocorners': isUnassigned,
											'task-card-bottom-completed': taskIsCompleted,
										}"
									>
										<div>
											<img
												src="/images/common/task-card-calendar.png"
												alt="Task Calendar"
											/>
											<span
												v-if="!taskIsCompleted"
												v-bind:class="{ 'task-card-past-due': isTaskPastDue() }"
												>{{
													$t('tasks_due_date', [
														dueDateDisplay(message.task.due),
													])
												}}</span
											>
											<span v-if="taskIsCompleted">
												{{
													$t('tasks_completed_date', [
														dueDateDisplay(message.task.completed),
													])
												}}
											</span>
										</div>
									</div>
									<div
										v-if="isUnassigned"
										class="task-card-sub-bottom pointer"
										@click.stop="claimTask(message.task)"
									>
										<span>{{ $t('tasks_action_claim') }}</span>
									</div>
								</div>

								<div v-if="isTaskRemoved && showTaskCard" class="task-card">
									<div class="task-card-top">
										<img
											class="task-card-check"
											src="/images/common/task-checkbox.png"
											alt=""
										/>
										<span id="removed-task-card-span">{{
											message.systemmessageparts.task_title
										}}</span>
									</div>
									<div class="removed-task-card-sub-bottom">
										<span>{{ $t('tasks_unavailable') }}</span>
									</div>
								</div>

								<EditMessage
									v-if="isEditing"
									:messageId="message.id"
									@success="setEditing(false)"
									@cancel="setEditing(false)"
									@hide="messageHover = false"
								/>

								<MessageText
									v-if="!isStyledSystemEvent && !isEditing"
									:message="message"
									:currentUser="store.auth.user"
								/>

								<div
									class="pinned-by"
									v-if="isPinned"
									v-text="
										$t('chat_pin_message_pinned_by', { User: whoPinnedMessage })
									"
								></div>
							</div>
						</MessageSendingState>
					</div>
				</div>

				<div
					v-if="
						message.systemevent && !isStyledSystemEvent && !lsStoppedMessage
					"
				>
					<div class="message">
						<div class="message-text-system">
							{{ message.message }}
						</div>
					</div>
				</div>

				<MeetingEndedCard
					v-if="lsStoppedMessage"
					:videosession="videosession"
					@replay="replayArchives"
					@participants="updateList(true)"
					:class="{
						'bottom-margin':
							needsThreadLine && !message.reactions && !readReceiptUsers.length,
					}"
				/>

				<Reactions :message="message" @react="handleReaction" />

				<MessageActions
					v-if="!isEditing"
					:message="message"
					:show="messageHover"
					:hideDelete="
						lsStoppedMessage ? !canDeleteAnyMessage : !canDeleteStandardMessage
					"
					:hideReply="hideReply"
					:hideForward="
						hideForwardAction || lsStoppedMessage || message.isFailed
					"
					:hideReact="lsStoppedMessage || message.isFailed"
					:hideEdit="!isEditable"
					:hidePin="hidePin"
					:trimActionArea="trimActionArea"
					:isPinned="isPinned"
					@reply="$emit('reply', message)"
					@react="handleReaction"
					@hide="messageHover = false"
					@edit="setEditing(true)"
					@emoji-picker-opened="$emit('lock-hover', message.id)"
					@emoji-picker-closed="handleEmojiPickerClosed"
				/>

				<ThreadLine
					v-if="needsThreadLine"
					:isStart="isParent"
					:hasQuote="isStartOfThreadSegment"
					:topDots="isStartOfThreadSegment && !isFirstChild && parentMessage"
					:bottomDots="isEndOfThreadSegment && (parentMessage || isParent)"
					:hasButton="isEndOfThreadSegment || isEndOfThread"
					:showBackdrop="messageHover"
				/>

				<div class="hover-time" v-if="hoverTime" v-text="hoverTime" />
			</div>

			<div
				class="view-thread-btn"
				v-if="showViewThread"
				@click="$emit('thread')"
				role="button"
			>
				{{ $t('chat_messageaction_viewthread') }}
			</div>
		</Accessible>

		<ReadReceipts
			:userIds="readReceipts"
			:showThreadLine="needsThreadLine && !showViewThread"
			:message="message"
			@click="updateList()"
		/>

		<UnreadDivider v-if="isLastViewedMsg" @viewed="$emit('viewed', $event)" />

		<div v-if="showFail" class="error-wrapper">
			<div class="error-alert">
				<span>{{ alertText }}</span>
				<input
					type="image"
					src="/images/common/white-icon-close.png"
					@click="showFail = false"
					alt="Close"
				/>
			</div>
		</div>

		<SimpleModal v-if="showArchives" @close="showArchives = false">
			<div class="archives-modal-wrapper">
				<div class="upload-title">
					{{ $t('chat_livestream_recordingspicker_title') }}
				</div>
				<div>
					<div
						class="settings-list-small"
						style="padding-left: 30px; padding-right: 30px; max-height: 175px;"
					>
						<div
							v-for="archive in reversedArchives"
							:key="archive.id"
							id="listRow"
							@click="playArchive(archive)"
						>
							<span style="padding-left: 10px;">{{
								archiveTime(archive)
							}}</span>
							<div id="playArrow"></div>
						</div>
					</div>
				</div>
			</div>
		</SimpleModal>

		<TaskDetail
			v-if="showTaskDetail"
			@close="showTaskDetail = false"
			@taskcomplete="completeTask"
			@taskuncomplete="unCompleteTask"
			v-bind:taskdata="taskData"
		/>

		<StandardModal
			v-if="showMesssageReactionLimit"
			open
			hideCancel
			:title="$t('message_reaction_limit_title')"
			:confirmText="$t('common_button_okay')"
			:confirmProps="{ loading }"
			@confirm="showMesssageReactionLimit = false"
			@cancel="showMesssageReactionLimit = false"
			small
			ref="modal"
			class="reaction-limit-modal"
		>
			<div class="modal-wrapper">
				<FaceSmileIcon halo type="regular" />
				{{
					$t('message_reaction_limit_description', {
						limit: MAX_REACTIONS_PER_MESSAGE,
					})
				}}
			</div>
		</StandardModal>

		<StandardModal
			v-if="showUserReactionLimit"
			open
			hideCancel
			:title="$t('message_user_reaction_limit_title')"
			:confirmText="$t('common_button_okay')"
			:confirmProps="{ loading }"
			@confirm="showUserReactionLimit = false"
			@cancel="showUserReactionLimit = false"
			small
			ref="modal"
			class="reaction-limit-modal"
		>
			<div class="modal-wrapper">
				<FaceSmileIcon halo type="regular" />
				{{
					$t('message_user_reaction_limit_description', {
						limit: MAX_REACTIONS_PER_USER,
					})
				}}
			</div>
		</StandardModal>
	</div>
</template>

<script>
import store, {
	getUser,
	setUser,
	updateMessage,
	getMessageIdsForTaskId,
	getMyUserId,
	getUpload,
	isParentMessage,
	getMessage,
	getChat,
	isSafari,
	getCurrentChat,
	getVideosession,
} from '../../store'
import { post } from '../../helpers/api'

import moment from 'moment'

import { SimpleModal, ProfilePic, UserName, StandardModal } from '../common'
import TaskDetail from '../TaskDetail'
import EditMessage from './EditMessage'
import MessageText from './MessageText'
import MessageTime from './MessageTime'
import ParentQuote from './ParentQuote'
import ThreadLine from './ThreadLine'
import DateDivider from './DateDivider'
import ReadReceipts from './ReadReceipts'
import MessageActions from './MessageActions'
import UnreadDivider from './UnreadDivider'
import Reactions from './Reactions'
import MeetingEndedCard from './MeetingEndedCard'
import MessageSendingState from './MessageSendingState'
import { isStyledSystemMessage } from './'
import MessageMedia from './MessageMedia'
import { buildSystemMessage, asyncIsElementInView } from '../../helperMethods'
import { addReaction, removeReaction } from '@/api/message'
import Accessible from '@/components/common/Accessible'
import { getBaseEmoji } from '@/helpers/emojis'
import { dayOfWeek, getLocalDate } from '@/helpers/utils'
import {
	MAX_REACTIONS_PER_USER,
	MAX_REACTIONS_PER_MESSAGE,
} from '@/helpers/constants'
import {
	chatCan,
	CREATE_MESSAGE,
	DELETE_ANY_MESSAGE,
	PIN_MESSAGE,
} from '@/helpers/permissions'
import { CircleExclamationIcon, ThumbtackIcon, FaceSmileIcon } from '@/icons'

const unstackableSystemEvents = {
	VIDEOSESSION_STOPPED: true,
}

const USER_MAX = 5

export default {
	components: {
		SimpleModal,
		TaskDetail,
		ProfilePic,
		MessageText,
		MessageTime,
		MessageActions,
		ParentQuote,
		ThreadLine,
		DateDivider,
		ReadReceipts,
		Reactions,
		UserName,
		MeetingEndedCard,
		Accessible,
		UnreadDivider,
		EditMessage,
		MessageMedia,
		MessageSendingState,
		CircleExclamationIcon,
		ThumbtackIcon,
		StandardModal,
		FaceSmileIcon,
	},

	props: [
		'message',
		'readReceipts',
		'skipAnimation',
		'hideReplyAction',
		'hideForwardAction',
		'hidePinAction',
		'previousMessage',
		'nextMessage',
		'hideThreadLine',
		'idPrefix',
		'isListVisible',
		'isLastViewedMsg',
		'trimActionArea',
		'isPinned',
		// because of the reverse stacking order of the message stream,
		// we have to assign a specific z-index to each message
		'zIndex',
		'hoverLockId',
	],

	data() {
		return {
			store,
			isEditing: false,
			messageHover: false,
			messageTabbing: false,
			showFail: false,
			showTaskDetail: false,
			alertText: '',
			taskData: '',
			showArchives: false,
			readReceiptUsers: [],
			USER_MAX,
			isMouseOverMessage: false,
			showMesssageReactionLimit: false,
			showUserReactionLimit: false,
			MAX_REACTIONS_PER_USER,
			MAX_REACTIONS_PER_MESSAGE,
		}
	},

	emits: [
		'thread',
		'reply',
		'edit',
		'sendFailed',
		'viewed',
		'lock-hover',
		'clear-hover',
	],

	watch: {
		messageHover: function (val) {
			if (!val) {
				var borderelm = document.querySelector(
					'.accessibility-highlight-border'
				)
				if (borderelm) {
					borderelm.style.display = 'none'
					borderelm.style.display = 'none'
				}
			}
		},

		zIndex(zIndex) {
			this.setStyle({ zIndex })
		},

		showTaskDetail(show) {
			this.setStyle({ zIndex: show ? 1000 : this.zIndex })
		},
	},

	mounted() {
		this.handleExpansion()

		if (
			this.message.task_id &&
			this.$route.query.task_id &&
			this.$route.query.task_id === this.message.task_id.toString() &&
			this.message.task_id !== this.store.taskShown
		) {
			this.getTaskDetail(this.message.task)
		}

		this.onAnimationEnd = async (e) => {
			// only listen for the end of the centered highlight animation
			if (!e.animationName.startsWith('centered-highlight-data')) return
			// once highlight has finished animating, update message in store
			await this.$nextTick()
			updateMessage(this.message.id, { isHighlighted: false })
		}
		this.$el.addEventListener('animationend', this.onAnimationEnd)

		this.setStyle({ zIndex: this.zIndex })
		const upload = getUpload(this.message.uuid)
		this.$el.style.setProperty(
			'--background',
			`url(${upload ? upload.blob : 'none'})`
		)

		this.$onEventBus({ editLatestMessage: this.onEditLatestMessage })
	},

	beforeUnmount() {
		this.$el.removeEventListener('animationend', this.onAnimationEnd)
	},

	computed: {
		hideReply() {
			return (
				this.hideReplyAction ||
				this.message.isFailed ||
				!chatCan(getCurrentChat(), CREATE_MESSAGE)
			)
		},

		inMeeting() {
			return this.$route.path.startsWith('/meeting')
		},

		isStacked() {
			const { message: current, previousMessage: prior } = this
			if (!prior) return false
			if (this.isPinned) return false
			if (current.isParent || current.isFailed) return false
			if (current.user_id !== prior.user_id) return false
			if (unstackableSystemEvents[current.systemevent]) return false
			if (unstackableSystemEvents[prior.systemevent]) return false
			if (
				current.parentmessage_id != prior.parentmessage_id &&
				current.parentmessage_id !== prior.id
			)
				return false
			if (prior.isParent && current.parentmessage_id !== prior.id) return false
			const minutesSinceLastMessage = moment
				.duration(moment(current.created_at).diff(prior.created_at))
				.asMinutes()
			if (minutesSinceLastMessage > 10) return false
			return true
		},

		videosession() {
			return getVideosession(this.message.videosession_id)
		},

		showDateDivider() {
			if (store.livestreaming.showLive) return false
			const { message: current, previousMessage: prior } = this
			if (!prior) return true // show date divider on first message since we show a new chat header
			const priorDate = getLocalDate(prior.created_at)
			const currDate = getLocalDate(current.created_at)
			return moment(priorDate).isBefore(currDate, 'day')
		},

		isParent() {
			return isParentMessage(this.message)
		},

		hasParent() {
			return this.message.parentmessage_id
		},

		parentMessage() {
			return this.hasParent && getMessage(this.message.parentmessage_id)
		},

		isFirstChild() {
			return (
				this.parentMessage &&
				this.message.id === this.parentMessage.firstchildmessage_id
			)
		},

		previousIsParent() {
			const { message, previousMessage: prev } = this
			return prev && prev.id === message.parentmessage_id
		},

		previousHasSameParent() {
			const { message, previousMessage: prev } = this
			return (
				prev &&
				prev.parentmessage_id &&
				prev.parentmessage_id === message.parentmessage_id
			)
		},

		nextHasSameParent() {
			const { message, nextMessage: next } = this
			return (
				next &&
				next.parentmessage_id &&
				// this message is the next message's parent
				(next.parentmessage_id === message.id ||
					// or we share the same parent
					next.parentmessage_id === message.parentmessage_id)
			)
		},

		needsThreadLine() {
			return (
				!this.hideThreadLine && (this.message.parentmessage_id || this.isParent)
			)
		},

		showViewThread() {
			return (
				this.needsThreadLine &&
				(this.isEndOfThreadSegment || this.isEndOfThread)
			)
		},

		isEndOfThread() {
			const { parentMessage: parent, message } = this
			return parent && parent.lastchildmessage_id === message.id
		},

		isStartOfThreadSegment() {
			return (
				this.hasParent && !this.previousHasSameParent && !this.previousIsParent
			)
		},

		isEndOfThreadSegment() {
			return (
				this.needsThreadLine && !this.isEndOfThread && !this.nextHasSameParent
			)
		},

		isMyMessage() {
			return this.user.id === store.auth.user.id
		},

		isEditable() {
			return !this.message.systemevent && this.isMyMessage
		},

		canDeleteAnyMessage() {
			const any =
				this.message.sendState === 'failed' ||
				chatCan(getCurrentChat(), DELETE_ANY_MESSAGE)
			return any
		},

		canDeleteStandardMessage() {
			return (
				this.canDeleteAnyMessage ||
				(this.isMyMessage &&
					this.message.systemevent !== 'VIDEOSESSION_STARTED')
			)
		},

		reversedArchives() {
			return this.videosession.archives.slice().reverse()
		},

		elid() {
			return (this.idPrefix || '') + 'message_' + this.message.id
		},

		hidePin() {
			return (
				this.hidePinAction ||
				this.message.isFailed ||
				!chatCan(getCurrentChat(), PIN_MESSAGE) ||
				!!this.message.systemevent
			)
		},

		whoPinnedMessage() {
			if (!this.isPinned) return
			const chat = getChat(this.message.bubble_id)
			if (!chat?.pinned_message_user_id) return {}
			const id = chat.pinned_message_user_id
			const user = getUser(id)
			return user?.fullname || {}
		},

		isUnassigned() {
			if (!this.message.task.assigneeuser_id) {
				return true
			} else {
				return false
			}
		},

		taskIsCompleted() {
			if (this.message.task.completed) {
				return true
			} else {
				return false
			}
		},

		showTaskCard() {
			if (
				this.message.systemevent === 'TASK_ASSIGNED' ||
				this.message.systemevent === 'TASK_DUEDATECHANGED'
			) {
				return true
			} else {
				if (
					this.message.task &&
					this.message.task.bubble_id === this.store.currentChatId &&
					!this.message.systemevent
				) {
					return true
				} else {
					return false
				}
			}
		},

		isTaskRemoved() {
			if (this.message.task_id) {
				if (
					this.message.task &&
					this.message.task.bubble_id === this.store.currentChatId
				) {
					return false
				} else {
					return true
				}
			} else {
				return false
			}
		},

		isStyledSystemEvent() {
			return isStyledSystemMessage(this.message)
		},

		systemMessage() {
			return buildSystemMessage(this.message)
		},

		systemLinkMessage() {
			const { systemevent } = this.message
			return ['BUBBLE_JOIN', 'BUBBLE_KICKED', 'BUBBLE_ADDED_MANY'].includes(
				systemevent
			)
		},

		systemMessagePrefix() {
			if (this.message.systemevent === 'BUBBLE_JOIN') {
				return 'added'
			} else {
				return 'removed'
			}
		},

		systemMessageSuffix() {
			return this.message.systemmessageparts.who_name
		},

		lsStoppedMessage() {
			return this.message.systemevent === 'VIDEOSESSION_STOPPED'
		},

		user() {
			return getUser(this.message.user_id) || {}
		},

		listElement() {
			// TODO: need a better way to reference the list element, other than
			// crawling up this $parent chain to avoid coupling 😞
			return this.$parent.$el
		},

		showBackdrop() {
			if (this.isEditing) return true
			return (
				this.messageHover &&
				!this.needsThreadLine &&
				!this.message.isHighlighted
			)
		},

		hoverTime() {
			if (!this.isStacked) return null
			return this.$m(this.message.created_at + 'Z').format('h:mm')
		},

		accessibilityTabOrder() {
			const onEscape = async (vm, item, e) => {
				e.stopPropagation()
				this.closeAccessibleActions()
				await vm.$nextTick()
				return null
			}

			return [
				{ css: '.user-display-name', Escape: onEscape },
				{ css: '.message-time', Escape: onEscape },
				{
					css: 'svg.reply',
					clickOnEnter: true,
					Escape: onEscape,
					Enter: () => {
						this.accessibilityMode = false
						setTimeout(() => EventBus.$emit('focus-editor'), 500)
					},
				},
				{
					css: 'svg.forward',
					clickOnEnter: true,
					Escape: onEscape,
				},
				{
					css: '.react',
					clickOnEnter: true,
					Enter: async () => {
						await new Promise((resolve) => setTimeout(resolve, 100))
						return [
							{
								all: '.emoji-container .reaction-item',
								clickOnEnter: true,
								Escape: async (vm, item, e) => {
									e.stopPropagation()
									vm.closeTray()
									this.closeAccessibleActions()
									await vm.$nextTick()
									return null
								},
							},
							{
								css: '.add-reaction',
							},
						]
					},
				},
				{ css: '.edit', clickOnEnter: true, Escape: onEscape },
				{ css: '.seen-by', clickOnEnter: true, Escape: onEscape },
				{ css: '.pin-message', clickOnEnter: true, Escape: onEscape },
				{ css: '.delete', clickOnEnter: true, Escape: onEscape },
				{ all: '.media-grid-item', clickOnEnter: true, Escape: onEscape },
				{ all: '.media-file-container', clickOnEnter: true, Escape: onEscape },
				{ css: '.pronto-visual-media', clickOnEnter: true, Escape: onEscape },
				{ css: '.attachment-wrapper', clickOnEnter: true, Escape: onEscape },
				{
					css: '.media-item-container img',
					clickOnEnter: true,
					Escape: onEscape,
				},
				{
					css: '.media-item-container video',
					clickOnEnter: true,
					Escape: onEscape,
				},
				{ css: '.task-card-check', clickOnEnter: true, Escape: onEscape },
				{ css: '.task-card-arrow', clickOnEnter: true, Escape: onEscape },
				{ css: '.task-card-sub-bottom', clickOnEnter: true, Escape: onEscape },
				{ css: '.message-content', Escape: onEscape },
				{ css: '.attachment-link', clickOnEnter: true, Escape: onEscape },
				{
					all: `.reactions-wrapper .reaction-emoji`,
					clickOnEnter: true,
					Escape: onEscape,
					ArrowUp: () => {
						const tip = document.querySelector('.tippy-box .reactions-tip')
						tip && tip.click()
						return null
					},
				},
				{ css: '.view-thread-btn', clickOnEnter: true },
				{ css: '.globe', clickOnEnter: true },
				{ css: '.read-receipts .profile-list-container', clickOnEnter: true },
			]
		},

		isHoverLocked() {
			return this.hoverLockId === this.message.id
		},
		ignoreHover() {
			return this.hoverLockId !== null && !this.isHoverLocked
		},
	},

	methods: {
		async openAccessibleActions() {
			EventBus.$emit('closeMessageActions')
			this.messageHover = true
			this.messageTabbing = true
		},

		async closeAccessibleActions() {
			this.messageTabbing = false
			this.messageHover = false
		},

		setStyle(styles = {}) {
			Object.assign(this.$el.style, styles)
		},

		setEditing(editing) {
			this.isEditing = editing
			this.$emit('edit', editing)
		},

		onEditLatestMessage({ messageId, threadId }) {
			if (this.message.id !== messageId) return
			if (threadId) {
				if (this.idPrefix?.startsWith('thread')) {
					this.isEditing = true
				}
			} else {
				this.isEditing = true
			}
		},

		updateList(live = false) {
			const ids = live ? this.videosession.users : this.readReceipts
			const label = live
				? this.$t('chat_meeting_participants')
				: this.$t('chat_readreceipts_title')
			EventBus.$emit('updateUserList', { ids, label })
		},

		async handleExpansion() {
			const isNew = !this.message.id
			if (this.skipAnimation || this.isEditing || !this.isListVisible)
				return this.quicklyRevealMessage()
			if (isNew) return this.animateExpansion()
			const inView = await asyncIsElementInView(this.$el, this.listElement)
			if (inView) return this.animateExpansion()
			this.quicklyRevealMessage()
		},

		expand() {
			window.requestAnimationFrame(() => {
				let height = this.$el.scrollHeight
				if (this.readReceipts.length) {
					// have to account for height of read receipts
					height += 16
				}

				this.setStyle({ height: height + 'px', opacity: 1 })
				const onTransitionEnd = (e) => {
					if (e.target !== this.$el) return
					this.$el.removeEventListener('transitionend', onTransitionEnd)
					// reset the inline styles to null so the css styles take effect
					this.setStyle({ height: null, overflow: null })
				}
				this.$el.addEventListener('transitionend', onTransitionEnd)
			})
		},

		animateExpansion() {
			// shrink message to 0px tall, then animate expansion
			this.setStyle({ height: 0, opacity: 0.25, overflow: 'hidden' })
			this.expand()
		},

		async quicklyRevealMessage() {
			this.setStyle({ opacity: 1 })
			if (isSafari) {
				// safari sucks
				// also, safari doesn't maintain scroll position when new msg come in
				// we have to fix the scroll to the right spot before + after msg appears
				if (this.listElement.scrollTop === 0) {
					// fix scroll to bottom of chat
					this.listElement.scrollTop = 0
				} else {
					// fix scroll to current position
					this.listElement.scrollTop =
						this.listElement.scrollTop - this.$el.scrollHeight
				}
			}
		},

		async handleReaction(emoji) {
			// Check user's reaction limit by counting unique emoji_base values
			const userReactionCount = new Set(
				Object.values(this.message.reactions || {})
					.filter((r) => r.me)
					.map((r) => r.emoji_base)
			).size

			// Check if this is a new reaction for the user
			const isNewReactionForUser = !Object.values(
				this.message.reactions || {}
			).find((r) => r.emoji_base === getBaseEmoji(emoji) && r.me)

			// If adding a new reaction would exceed user's limit
			if (isNewReactionForUser && userReactionCount >= MAX_REACTIONS_PER_USER) {
				this.showUserReactionLimit = true
				return
			}

			// Check total unique reactions on message by counting unique emoji_base values
			const uniqueReactionsSet = new Set(
				Object.values(this.message.reactions || {}).map((r) => r.emoji_base)
			)

			const totalUniqueReactions = uniqueReactionsSet.size
			const isEmojiAlreadyUsed = uniqueReactionsSet.has(emoji)

			// If adding a new reaction would exceed message's limit
			if (
				!isEmojiAlreadyUsed &&
				isNewReactionForUser &&
				totalUniqueReactions >= MAX_REACTIONS_PER_MESSAGE
			) {
				this.showMesssageReactionLimit = true
				return
			}

			// Find reaction by iterating through reaction keys
			const reactions = this.message.reactions || {}
			const reaction = Object.entries(reactions).find(
				([_, r]) => r.emoji === emoji
			)?.[1]

			this.messageHover = false

			if (reaction?.me) {
				// optimistically remove my reaction
				this.removeMyReaction(emoji)
				try {
					// make api call to remove my reaction
					await removeReaction(this.message.id, emoji)
				} catch (e) {
					console.error(e)
					// there was an error so undo optimistic removal
					this.addMyReaction(emoji)
				}
			} else {
				// optimistically add my reaction
				this.addMyReaction(emoji)
				try {
					// make api call to add my reaction
					await addReaction(this.message.id, emoji)
				} catch (e) {
					console.error(e)
					// there was an error so undo optimistic addition
					this.removeMyReaction(emoji)
				}
			}
		},

		addMyReaction(emoji) {
			const myId = getMyUserId()
			updateMessage(this.message.id, (msg) => {
				const reactions = msg.reactions || {}
				const existingReaction = Object.values(reactions).find(
					(r) => r.emoji === emoji
				)

				if (existingReaction) {
					// Update existing reaction
					const updatedReactions = { ...reactions }
					const key = Object.keys(reactions).find(
						(k) => reactions[k].emoji === emoji
					)
					updatedReactions[key] = {
						...existingReaction,
						me: true,
						count: existingReaction.count + 1,
						users: [...existingReaction.users, myId],
						id: emoji,
						emoji_base: getBaseEmoji(emoji),
					}
					msg.reactions = updatedReactions
				} else {
					// Add new reaction
					// const newKey = Object.keys(reactions).length + 1
					msg.reactions = {
						...reactions,
						[emoji]: {
							emoji: emoji,
							me: true,
							count: 1,
							users: [myId],
							emoji_base: getBaseEmoji(emoji),
						},
					}
				}
				return msg
			})
		},

		removeMyReaction(emoji) {
			const myId = getMyUserId()
			updateMessage(this.message.id, (msg) => {
				const reactions = { ...msg.reactions }
				const key = Object.keys(reactions).find(
					(k) => reactions[k].emoji === emoji
				)

				if (key) {
					const reaction = reactions[key]
					reactions[key] = {
						...reaction,
						me: false,
						count: reaction.count - 1,
						users: reaction.users.filter((id) => id !== myId),
					}

					// Remove reaction if count is 0
					if (reactions[key].count === 0) {
						delete reactions[key]
					}
				}

				msg.reactions = reactions
				return msg
			})
		},

		buildProfileCard(user) {
			EventBus.$emit('openProfileCard', user)
		},

		getProfileCard(user_id) {
			// let's check for the user locally first including our own...
			const user = getUser(user_id)
			if (user) {
				EventBus.$emit('openProfileCard', user)
			} else {
				post('/api/v1/user.detail', { id: user_id })
					.then(({ data }) => {
						const user = setUser(data.user)
						EventBus.$emit('openProfileCard', user)
					})
					.catch((error) => {
						console.error(error)
					})
			}
		},

		dueDateDisplay(date) {
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			const DOW = this.dayOfWeek(date)
			return ['TODAY', 'TOMORROW', 'YESTERDAY'].includes(DOW)
				? this.$t(`common_${DOW.toLowerCase()}`).toLowerCase()
				: moment(date).local().format('LL')
		},

		dayOfWeek(date) {
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			return dayOfWeek(date)
		},

		taskAssigned(task) {
			if (task.assigneeuser_id) {
				let user = this.store.getUser(task.assigneeuser_id)
				return this.store.getUserName(user)
			} else {
				return this.$t('chat_message_unknownuser')
			}
		},

		deleteTask(data) {
			if (data.completed) {
				this.store.tasks.completed = this.store.tasks.completed.filter(
					(task) => task.id !== data.task_id
				)
			} else {
				this.store.tasks.open = this.store.tasks.open.filter(
					(task) => task.id !== data.task_id
				)
			}

			this.store.updateTaskStats()
		},

		completeTask(task) {
			this.deleteTask({ task_id: task.id, completed: false })

			task.completed = moment.utc().format('YYYY-MM-DD')
			const taskMessageIds = getMessageIdsForTaskId(task.id)
			taskMessageIds.forEach((messageId) => {
				updateMessage(messageId, { task })
			})

			post('/api/v1/task.complete', { task_id: task.id })
				.then(({ data }) => {
					taskMessageIds.forEach((messageId) => {
						updateMessage(messageId, { task: data.task })
					})
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error()
					// eslint-disable-next-line vue/no-mutating-props
					this.message.task.completed = null
				})
		},

		unCompleteTask(task) {
			task.completed = null
			// eslint-disable-next-line vue/no-mutating-props
			this.message.task.completed = null

			const taskMessageIds = getMessageIdsForTaskId(task.id)
			taskMessageIds.forEach((messageId) => {
				updateMessage(messageId, { task })
			})

			post('/api/v1/task.uncomplete', { task_id: task.id })
				.then(({ data }) => {
					taskMessageIds.forEach((messageId) => {
						updateMessage(messageId, { task: data.task })
					})
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error()
					// eslint-disable-next-line vue/no-mutating-props
					this.message.task.completed = task.completed
				})
		},

		getTaskDetail(task) {
			this.store.taskShown = task.id
			if (this.store.config.wrapper) {
				window.open(
					window.location.href + '?task_id=' + task.id,
					'_blank',
					'toolbar=yes, menubar=yes, scrollbars=yes, status=yes, titlebar=yes, top=500, fullscreen=yes'
				)
				return
			}

			this.taskData = task
			if (task.bubble_id) {
				const chat = getChat(task.bubble_id)
				if (chat) {
					this.taskData.assignedTitle = chat.title
				} else {
					this.taskData.assignedTitle = this.$t('chat_message_unknownuser')
				}
			} else {
				this.taskData.assignedTitle = this.taskAssigned(task)
			}

			if (!task.user) {
				this.taskData.user = []
			} else {
				this.taskData.user = task.user
			}

			if (!task.taskmedia) {
				this.taskData.taskmedia = []
			} else {
				this.taskData.taskmedia = task.taskmedia
			}

			this.taskData.parent = 'Message.vue'
			this.showTaskDetail = true
		},

		claimTask(task) {
			task.assigneeuser_id = store.auth.user.id

			getMessageIdsForTaskId(task.id).forEach((messageId) => {
				updateMessage(messageId, { task })
			})

			post('/api/v1/task.claim', {
				task_id: task.id,
				organization_id: store.auth.user.organizations[0].id,
			})
				.then(({ data }) => {
					getMessageIdsForTaskId(task.id).forEach((messageId) => {
						updateMessage(messageId, { task: data.task })
					})
				})
				.catch((error) => {
					console.error(error)
					task.assigneeuser_id = null
					getMessageIdsForTaskId(task.id).forEach((messageId) => {
						updateMessage(messageId, { task })
					})
					this.$alert.error()
				})
		},

		isTaskPastDue() {
			if (this.message.task.completed) return false
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			return moment().isAfter(this.message.task.due, 'day')
		},

		mediaId(messagemedia) {
			return 'media_' + messagemedia.id
		},

		archiveTime(archive) {
			if (archive.duration > 60 && archive.duration < 3600) {
				var duration = '(' + Math.floor(archive.duration / 60) + ' min)'
			} else if (archive.duration > 3600) {
				var duration = '(' + Math.floor(archive.duration / 3600) + ' hour)'
			} else {
				var duration = '(' + archive.duration + ' sec)'
			}
			return moment(archive.starttime + 'Z').format('LT') + ' ' + duration
		},

		replayArchives() {
			if (this.videosession.archives.length > 1) {
				this.showArchives = true
			} else {
				this.$videoModal(store.mediaPath(this.videosession.archives[0]))
			}
		},

		playArchive(archive) {
			this.$videoModal(store.mediaPath(archive))
			this.showArchives = false
		},

		hasUrlCardOnly() {
			if (this.message.resource && this.isStacked) {
				let regexp = /(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?/

				if (regexp.test(this.message.message)) {
					let matches = this.message.message.match(regexp)
					if (matches[0] === this.message.message) {
						return true
					} else {
						return false
					}
				} else {
					return false
				}
			} else {
				return false
			}
		},

		onMouseEnter() {
			if (!this.ignoreHover) {
				this.messageHover = true
			}
			this.isMouseOverMessage = true
		},
		onMouseLeave() {
			if (!this.ignoreHover && !this.isHoverLocked) {
				this.messageHover = false
			}
			this.isMouseOverMessage = false
		},
		handleEmojiPickerClosed() {
			this.$emit('clear-hover')

			if (!this.isMouseOverMessage) {
				this.messageHover = false
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.overflow-unset {
	overflow: unset !important;
}

.editing-message {
	max-width: unset !important;
	overflow: visible;
}

.message-wrapper:hover .lsPublishers img {
	border-color: var(--neutral-0);
}
.message-wrapper {
	padding-left: 10px;
	padding-top: 4px;
	padding-bottom: 4px;
	position: relative;
	border-radius: 6px;
	&.message-hover {
		background: var(--neutral-100);
		:deep(.unfurl-concat:after) {
			background-color: var(--neutral-100) !important;
		}
		:deep(.unfurl-concat:before) {
			background-color: var(--neutral-100) !important;
		}
		:deep(.message-img) .online-indicator {
			border: 3px solid var(--neutral-100);
		}
		.message-img {
			border-color: var(--neutral-100);
		}
		.hover-time {
			opacity: 1;
		}
	}
	&.thread-msg-hover {
		background: var(--neutral-100);
		.message-img {
			border-color: var(--neutral-100);
			:deep(.online-indicator) {
				border: 3px solid var(--neutral-100);
			}
		}
		.hover-time {
			opacity: 1;
		}
	}

	.hover-time {
		position: absolute;
		top: 8px;
		left: 4px;
		font-size: 10px;
		opacity: 0;
		color: var(--neutral-400);
	}
}

@keyframes centered-highlight {
	0% {
		background: rgba(#ffbb00, 0.18);
		transform: scale(1);
	}
	10% {
		transform: scale(1.02);
	}
	20% {
		transform: scale(1);
	}
	80% {
		background: rgba(#ffbb00, 0.18);
	}
	100% {
		background: rgba(#ffbb00, 0);
	}
}

@keyframes centered-highlight-border {
	0% {
		border-color: rgba(#ffbb00, 0.08);
	}
	80% {
		border-color: rgba(#ffbb00, 0.08);
	}
	100% {
		border-color: rgba(#ffbb00, 0);
	}
}

.message-outer-container {
	position: relative;
	opacity: 0.25;
	transition: all 0.3s ease-in-out;
	transition-property: height, opacity;
	flex-shrink: 0;
	&:first-child {
		:deep(.read-receipts.has-receipts) {
			margin-bottom: 0;
		}
	}
	&.highlighted {
		border-radius: 10px;
		animation: centered-highlight 5s ease-out;
		:deep(.message-img) {
			animation: centered-highlight-border 5s ease-out;
		}
	}
	&.no-animate {
		// effectively disables animations without disabling the
		// transitionend event we are listening to elsewhere
		transition-duration: 0 !important;
	}
}

.btn-remove {
	outline: 0;
}

.btn-remove::-moz-focus-inner {
	border: 0;
}

.media-sending {
	opacity: 0.6;
	:deep(.media-item-image) {
		text-indent: -100000px;
	}
}

.message-spinner {
	position: absolute;
	z-index: 1;
}

.message-container,
.message-forward-btn,
.message-forward-btn img,
.message-react-btn,
.message-react-btn img,
.message-delete-btn,
.message-delete-btn img,
.message-img,
.message-img-missing,
.message-text-system,
.read-receipt-container,
.reactions-receipts {
	user-select: none;
}

.message-container {
	position: relative;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	z-index: 2; // has to live above thread lines & backdrop
}

.message-img {
	flex-shrink: 0;
	margin-top: 0;
	margin-right: 6px;
	cursor: pointer;
	border: 4px solid var(--neutral-0);
}

.message {
	width: 100%;
	flex-grow: 1;
	margin-left: 0;
	.message-header {
		display: flex;
		align-items: center;
		margin-bottom: 4px;
		.message-name {
			font-family: Avenir-Heavy;
			font-size: 15px;
			color: var(--neutral-800);
			display: inline-block;
			&:hover {
				text-decoration: underline;
			}
		}

		.pinned-banner {
			background-color: var(--blue-100);
			border-radius: 40px;
			font-size: 12px;
			color: var(--blue-400);
			font-family: Avenir-Heavy;
			padding: 5px 8px;
			display: flex;
			align-items: center;
			justify-content: center;
			margin-left: 8px;
			.pin-icon {
				margin-right: 3px;
			}
		}
	}

	.pinned-by {
		margin-top: 8px;
		color: var(--blue-400);
		font-size: 14px;
		font-family: Avenir-Heavy;
	}
}

.stacked .message {
	margin-left: 52px;
	.message-header {
		display: none;
	}
}

.stacked .message-actions-wrapper {
	top: -12px;
}

.meeting-started-message {
	font-family: 'Avenir-HeavyOblique';
	font-size: 14px;
	font-weight: 900;
	font-stretch: normal;
	font-style: oblique;
	line-height: normal;
	letter-spacing: normal;
	color: #b7b9bd;
}
.archives-modal-wrapper {
	width: 500px;
	padding: 50px;
	#listRow {
		text-align: left;
	}
}

.message-text {
	@include fontFamily(400);
	font-size: 17px;
	color: var(--neutral-800);
	width: 100%;

	@media screen and (max-width: 800px) {
		width: 100% !important;
	}
}

.message-text-system {
	margin-top: 4px;
	font-family: Avenir-Oblique;
	font-size: 14px;
	color: #b7b9bd;
}

.message-text-system-new {
	font-family: Avenir-Oblique;
	font-size: 17px;
	color: var(--neutral-400);
	font-weight: normal !important;
}
.message-text-system-new span {
	text-decoration: underline;
	cursor: pointer;
}

.view-thread-btn {
	cursor: pointer;
	position: relative;
	z-index: 1;
	font-size: 14px;
	font-family: Avenir-Black;
	color: var(--teal-300);
	margin: 0 0 10px 33px;
	display: inline-block;
}

.meeting-card-wrapper {
	position: relative;
	z-index: 2;
	margin-left: 4px;
	&.bottom-margin {
		margin-bottom: 16px;
	}
}

:deep(.media-item-image) {
	background-image: var(--background);
	background-size: cover;
}

.failedwrapper {
	:deep(.media-item-image) {
		opacity: 1;
		text-indent: -100000px;
	}
	:deep(.preview-spinner) {
		display: none;
	}
	.message-img {
		display: block;
		font-size: 36px;
		border-color: transparent;
		color: var(--red-400);
		margin: 0 12px 0 4px;
	}
}

.reaction-limit-modal {
	.modal-wrapper {
		@include flex-column-center;
		max-width: 300px;
		margin: 24px auto;
		text-align: center;
		.pronto-icon {
			color: var(--blue-300);
			font-size: 45px;
			margin-bottom: 24px;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.message-wrapper {
		&.message-hover,
		&.thread-msg-hover {
			background: var(--neutral-0);
			.message-img {
				border-color: var(--neutral-0);
				.online-indicator {
					border-color: var(--neutral-0);
				}
			}
			.meeting-card-wrapper {
				.profile-pic-avatar {
					border-color: var(--neutral-0) !important;
				}
			}
		}
		&.failedwrapper {
			background: var(--neutral-200);
			.message-img {
				border-color: var(--neutral-200);
			}
		}
		.message-img {
			border-color: var(--neutral-100);
			.online-indicator {
				border-color: var(--neutral-100);
			}
		}

		@keyframes centered-highlight {
			0% {
				background: rgba(#ffbb00, 0.35);
				transform: scale(1);
			}
			10% {
				transform: scale(1.02);
			}
			20% {
				transform: scale(1);
			}
			80% {
				background: rgba(#ffbb00, 0.35);
			}
			100% {
				background: rgba(#ffbb00, 0);
			}
		}

		@keyframes centered-highlight-border {
			0% {
				border-color: rgba(#ffbb00, 0.17);
			}
			80% {
				border-color: rgba(#ffbb00, 0.17);
			}
			100% {
				border-color: rgba(#ffbb00, 0);
			}
		}

		.removed-task-card-sub-bottom {
			border-color: var(--neutral-40040) !important;
		}

		.task-card-bottom-completed {
			border-color: var(--neutral-40040) !important;
			background-color: var(--neutral-100) !important;
		}

		.task-card-top-completed {
			border-color: var(--neutral-40040) !important;
			background-color: var(--neutral-100) !important;
		}
	}
}
</style>
